import { Suspense, lazy, type FC, type ReactNode } from "react";
import type { Placement } from "@floating-ui/react";
import { cx } from "../../utils/classnames";
// Lazy load Tooltip components
const Tooltip = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.Tooltip }))
);
const TooltipContent = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipContent }))
);
const TooltipTrigger = lazy(() =>
  import("../Tooltip").then((module) => ({ default: module.TooltipTrigger }))
);
export enum BadgeVariant {
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  ERROR = "ERROR",
  WARNING = "WARNING",
  SUCCESS = "SUCCESS",
  OUTLINE = "OUTLINE"
}

export enum BadgeSize {
  SM = "SM",
  BASE = "BASE",
  LG = "LG"
}

interface BadgeProps {
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  children?: React.ReactNode;

  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;

  variant: BadgeVariant;
  size: BadgeSize;

  tooltip?: ReactNode;
  tooltipClassName?: string;
  placement?: Placement;
}

const badgeVariant = {
  [BadgeVariant.BASE]: "bg-alpha-40 text-content-700 stroke-content-700",
  [BadgeVariant.PRIMARY]: "bg-primary-20 text-primary-600 stroke-primary-600",
  [BadgeVariant.ERROR]: "bg-danger-50 text-danger-500 stroke-danger-500",
  [BadgeVariant.WARNING]: "bg-warning-50 text-warning-700 stroke-warning-700",
  [BadgeVariant.SUCCESS]: "bg-success-50 text-success-700 stroke-success-700",
  [BadgeVariant.OUTLINE]:
    "text-content-700 stroke-content-700 bg-white border-alpha-100 border"
};

const badgeSize = {
  [BadgeSize.SM]: "text-xs px-2 py-0.5 rounded-2xl",
  [BadgeSize.BASE]: "text-sm px-2.5 py-0.5 rounded-2xl",
  [BadgeSize.LG]: "text-base px-3 py-1 rounded-2xl"
};

export const Badge: FC<BadgeProps> = ({
  children,
  className,
  style,
  onClick,
  variant,
  size,
  leftElement,
  rightElement,
  tooltip,
  tooltipClassName,
  placement = "right"
}) => {
  const clickProps = onClick
    ? {
        onClick,
        tabIndex: 1
      }
    : {};

  if (tooltip) {
    return (
      <Suspense fallback={null}>
        <Tooltip placement={placement}>
          <TooltipTrigger className={className}>
            <div
              className={cx(
                "inline-flex gap-1 truncate font-medium",
                badgeVariant[variant],
                badgeSize[size],
                className
              )}
              {...clickProps}
            >
              {leftElement ? leftElement : null}
              {children}
              {rightElement ? rightElement : null}
            </div>
          </TooltipTrigger>
          <TooltipContent
            className={cx(
              "bg-alpha-800 rounded-lg border p-4 text-sm text-white",
              tooltipClassName
            )}
          >
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </Suspense>
    );
  }

  return (
    <div
      className={cx(
        "inline-flex gap-1 truncate font-medium",
        badgeVariant[variant],
        badgeSize[size],
        className
      )}
      style={style}
      {...clickProps}
    >
      {leftElement ? leftElement : null}
      {children}
      {rightElement ? rightElement : null}
    </div>
  );
};
